import type { InterventionHide } from '@ehr/shared';

/**
 * ehr#682 (PROGRESS_NOTE_10-20-V141 V127): Show / hide for each intervention on the practice list.
 * Hidden ones stay off the "Interventions used" checklist, for all clients or for one client (a
 * client's own list wins, as Quick add does). Hidden only hides: a ticked one always shows, and the
 * "Search or add an intervention" box still finds it (hidden ones first).
 */
export const EMPTY_INTERVENTION_HIDE: InterventionHide = { all: [], clients: {} };

/** The hidden list this client's note uses: the client's own, else the all-clients one. */
export function hiddenFor(h: InterventionHide | undefined, contactId: string): string[] {
  if (!h) return [];
  return h.clients[contactId] ?? h.all;
}

/** The checklist's items: hidden ones drop out unless ticked. Order never changes. */
export function visibleInterventions(
  items: readonly string[],
  hidden: readonly string[],
  ticked: readonly string[],
): string[] {
  return items.filter((x) => !hidden.includes(x) || ticked.includes(x));
}

/**
 * Saves a Show / hide choice. "client" keeps it as this client's own list; "all" makes it the
 * all-clients list and this client follows it again (other clients' own lists stay theirs).
 */
export function saveHidden(
  h: InterventionHide,
  contactId: string,
  hidden: readonly string[],
  scope: 'client' | 'all',
): InterventionHide {
  const { [contactId]: _own, ...others } = h.clients;
  return scope === 'all'
    ? { all: [...hidden], clients: others }
    : { all: h.all, clients: { ...h.clients, [contactId]: [...hidden] } };
}

/** After the practice list changes: renamed items keep their hidden state, deleted ones go. */
export function fixHidden(
  h: InterventionHide,
  renames: Readonly<Record<string, string>>,
  deleted: ReadonlySet<string>,
): InterventionHide {
  const fix = (l: readonly string[]) =>
    Array.from(new Set(l.filter((x) => !deleted.has(x)).map((x) => renames[x] ?? x)));
  return {
    all: fix(h.all),
    clients: Object.fromEntries(Object.entries(h.clients).map(([c, l]) => [c, fix(l)])),
  };
}

/** V127: ticking a hidden one from search brings it back for this client. */
export function unhideFor(h: InterventionHide, contactId: string, item: string): InterventionHide {
  return saveHidden(
    h,
    contactId,
    hiddenFor(h, contactId).filter((x) => x !== item),
    'client',
  );
}

/** Same lists, ignoring order. */
export const sameHidden = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));
